import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/providers';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PreAuthNavbar } from '@/components/navigation/PreAuthNavbar';
import { Footer } from '@/components/marketing/Footer';

type VerificationState = 'verifying' | 'success' | 'error' | 'missing-token';

export function EmailVerificationPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { verifyEmail, isAuthenticated, user, isLoading } = useAuth();
    const [state, setState] = useState<VerificationState>('verifying');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const hasRun = useRef(false);
    const redirectPending = useRef(false);

    const token = searchParams.get('token');

    useEffect(() => {
        // Handle missing token case first
        if (!token) {
            setState('missing-token');
            return;
        }

        // Early exit conditions
        if (hasRun.current || state !== 'verifying') {
            return;
        }

        // Mark as run immediately to prevent multiple executions
        hasRun.current = true;

        const performVerification = async () => {
            console.log('Starting email verification with token:', token);

            try {
                await verifyEmail(token);
                console.log('Email verification successful');

                setState('success');
                toast.success('Email verified successfully!');
                redirectPending.current = true;

                // Don't redirect immediately - wait for auth state to update
                console.log('Email verification complete, waiting for auth state update...');
            } catch (error: any) {
                console.error('Email verification failed:', error);

                setState('error');
                const message = error?.message || 'Email verification failed';
                setErrorMessage(message);
                toast.error(message);
            }
        };

        performVerification();
    }, [token, verifyEmail, navigate, state]);

    // Handle redirect after auth state is updated
    useEffect(() => {
        if (redirectPending.current &&
            state === 'success' &&
            isAuthenticated &&
            user?.emailVerified &&
            !isLoading) {

            console.log('Auth state updated, redirecting to /app');
            redirectPending.current = false;

            // Small delay to ensure everything is settled
            setTimeout(() => {
                navigate('/app', { replace: true });
            }, 1500);
        }
    }, [isAuthenticated, user?.emailVerified, isLoading, state, navigate]);

    // Additional safety check - if user is already authenticated and email verified,
    // redirect them immediately
    useEffect(() => {
        if (isAuthenticated && user?.emailVerified && state === 'verifying' && !hasRun.current) {
            console.log('User already authenticated and verified, redirecting to /app');
            setState('success');
            toast.success('Email already verified!');
            setTimeout(() => {
                navigate('/app', { replace: true });
            }, 1000);
        }
    }, [isAuthenticated, user?.emailVerified, state, navigate]);

    const handleGoToLogin = () => {
        navigate('/login', { replace: true });
    };

    const handleGoToApp = () => {
        navigate('/app', { replace: true });
    };

    const renderContent = () => {
        switch (state) {
            case 'verifying':
                return (
                    <div className="text-center space-y-4">
                        <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                            <Loader2 className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
                        </div>
                        <h1 className="text-2xl font-bold">Verifying your email...</h1>
                        <p className="text-muted-foreground">
                            Please wait while we verify your email address.
                        </p>
                    </div>
                );

            case 'success':
                return (
                    <div className="text-center space-y-4">
                        <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                        </div>
                        <h1 className="text-2xl font-bold text-green-600 dark:text-green-400">
                            Email verified!
                        </h1>
                        <p className="text-muted-foreground">
                            Your email has been successfully verified. You'll be redirected to your dashboard shortly.
                        </p>
                        <div className="space-y-2">
                            <Button onClick={handleGoToApp} className="w-full">
                                Go to Dashboard
                            </Button>
                        </div>
                    </div>
                );

            case 'error':
                return (
                    <div className="text-center space-y-4">
                        <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900 rounded-full flex items-center justify-center">
                            <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
                        </div>
                        <h1 className="text-2xl font-bold text-red-600 dark:text-red-400">
                            Verification failed
                        </h1>
                        <p className="text-muted-foreground">
                            {errorMessage || 'The verification link is invalid or has expired.'}
                        </p>
                        <div className="space-y-2">
                            <Button onClick={handleGoToLogin} className="w-full">
                                Go to Login
                            </Button>
                            <p className="text-xs text-muted-foreground">
                                You can request a new verification email after logging in.
                            </p>
                        </div>
                    </div>
                );

            case 'missing-token':
                return (
                    <div className="text-center space-y-4">
                        <div className="mx-auto w-16 h-16 bg-yellow-100 dark:bg-yellow-900 rounded-full flex items-center justify-center">
                            <XCircle className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
                        </div>
                        <h1 className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                            Invalid verification link
                        </h1>
                        <p className="text-muted-foreground">
                            The verification link appears to be incomplete or malformed.
                        </p>
                        <div className="space-y-2">
                            <Button onClick={handleGoToLogin} className="w-full">
                                Go to Login
                            </Button>
                            <p className="text-xs text-muted-foreground">
                                You can request a new verification email after logging in.
                            </p>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen flex flex-col">
            <PreAuthNavbar />
            <main className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md">
                    {renderContent()}
                </div>
            </main>
            <Footer />
        </div>
    );
}

export default EmailVerificationPage;
